import { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import type { ShareInfo } from '../../shared/types';
import { api, post, setShareToken } from '../lib/api';
import { BrandLogo, LegalFooter, useTitle } from '../components/AppShell';
import { ClientBrandHeader, ClientTheme } from '../components/ClientBrand';
import { GuestProvider } from '../components/GuestProvider';
import { PresentationView } from '../components/Presentation';
import { errorMessage } from '../components/toast';
import { Field, LogoTile, Notice, Spinner } from '../components/ui';
import { useCampaign } from './CampaignPage';

function PasswordGate({ token, info }: { token: string; info: ShareInfo }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="auth-panel" style={{ minHeight: '100vh' }}>
      <form
        className="auth-box"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await post(`/api/share/${token}/unlock`, { password });
            await queryClient.invalidateQueries({ queryKey: ['share-info', token] });
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <ClientTheme color={info.client.brandColor} />
        {info.client.logoUrl ? (
          <LogoTile src={info.client.logoUrl} alt={info.client.name} width={240} height={72} />
        ) : (
          <span className="eyebrow">{info.client.name}</span>
        )}
        <div className="stack" style={{ gap: 6 }}>
          <h2 className="row">
            <Lock size={22} /> Geschützter Bereich
          </h2>
          <p className="muted">Bitte gib das Passwort ein, das du von der Agentur bekommen hast.</p>
        </div>
        {error && <Notice kind="error">{error}</Notice>}
        <Field label="Passwort">
          {(id) => (
            <input
              id={id}
              className="input"
              type="password"
              autoFocus
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <button className="btn btn-primary btn-block btn-arrow" disabled={busy} style={{ height: 46 }}>
          Öffnen
        </button>
      </form>
    </div>
  );
}

/** Rahmen für alle Seiten hinter einem Freigabe-Link: Token setzen, ggf. Passwort abfragen. */
export function ShareLayout() {
  const { token = '' } = useParams();
  // Synchron setzen, bevor Kind-Komponenten ihre Abfragen starten.
  setShareToken(token);
  useEffect(() => () => setShareToken(null), []);
  const info = useQuery({
    queryKey: ['share-info', token],
    queryFn: () => api<ShareInfo>(`/api/share/${token}`),
    retry: false,
    staleTime: 60_000,
  });

  if (info.isLoading) return <Spinner />;
  if (info.error || !info.data) {
    return (
      <div className="auth-panel" style={{ minHeight: '100vh' }}>
        <div className="auth-box">
          <BrandLogo />
          <h2>Link nicht verfügbar</h2>
          <Notice kind="warn">{errorMessage(info.error)}</Notice>
          <p className="muted">Bitte frag bei der Agentur nach einem neuen Link.</p>
        </div>
      </div>
    );
  }
  if (info.data.requiresPassword && !info.data.unlocked)
    return <PasswordGate token={token} info={info.data} />;

  const { client } = info.data;
  return (
    <GuestProvider>
      <ClientTheme color={client.brandColor} />
      <ClientBrandHeader client={client} campaignName={info.data.campaignName} />
      <main>
        <Outlet context={info.data} />
      </main>
    </GuestProvider>
  );
}

export function ShareCampaignPage() {
  const { token = '' } = useParams();
  const info = useOutletContext<ShareInfo>();
  const campaign = useCampaign(info.campaignId);
  useTitle(campaign.data?.name);
  if (campaign.isLoading) return <Spinner />;
  if (!campaign.data) {
    return (
      <div className="page">
        <Notice kind="error">{errorMessage(campaign.error)}</Notice>
      </div>
    );
  }
  return (
    <>
      <PresentationView campaign={campaign.data} base={`/r/${token}`} />
      <LegalFooter neutral={info.client.presenter.mode !== 'agency'} />
    </>
  );
}
